import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import useHandlePopoverClick from '../../hooks/useHandlePopoverClick';
import {
    startOfMonth,
    endOfMonth,
    startOfWeek,
    endOfWeek,
    eachDayOfInterval,
    addMonths,
    subMonths,
    isSameDay,
    isSameMonth,
    isToday,
    format,
    differenceInCalendarDays,
    addDays,
} from 'date-fns';
import {
    draggable,
    dropTargetForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';

import { showSnackBar } from '../../redux/Slices/snackbarSlice';
import { relative } from '../../utils/helpers';
import { config } from '../../config';
import KeyboardArrowDown from '../icons/KeyboardArrowDown';
import Add from '../icons/Add';
import Close from '../icons/Close';
import './scss/BoardCalendarView.scss';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MAX_ROWS_PER_DAY = 3; // bars/chips shown before "+N more"

// Normalize a value to a Date at local midnight (so day comparisons are stable).
const toDay = (v) => {
    if (!v) return null;
    const d = new Date(v);
    if (Number.isNaN(d.getTime())) return null;
    d.setHours(0, 0, 0, 0);
    return d;
};

// Flatten board lists → cards, tagging each with its list for color/context.
const flattenCards = (board) => {
    if (!board?.actionLists?.length) return [];
    return board.actionLists.flatMap((list) =>
        (list.cards || []).map((card) => ({ ...card, _list: list }))
    );
};

// A small, pleasant palette for cards without a label color, so dots vary
// instead of all being the same brand blue. Deterministic per card.
const DOT_PALETTE = [
    '#4f86ff', // blue
    '#34c38f', // green
    '#f1963c', // orange
    '#e564a6', // pink
    '#9b6ef3', // purple
    '#22b8cf', // cyan
    '#f0596d', // red
    '#a8a532', // olive
];

const hashId = (str) => {
    let h = 0;
    for (let i = 0; i < str.length; i += 1) h = (h * 31 + str.charCodeAt(i)) >>> 0;
    return h;
};

const cardColor = (card) => {
    const label = Array.isArray(card.labels) ? card.labels.find((l) => l?.color) : null;
    if (label?.color) return label.color;
    const id = card.shortId || card._id || card.title || '';
    return DOT_PALETTE[hashId(String(id)) % DOT_PALETTE.length];
};

const BoardCalendarView = ({ board, setBoard, isReadOnly = false, navbarHeight = 0 }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { handlePopoverClick } = useHandlePopoverClick();
    const [cursor, setCursor] = useState(() => startOfMonth(new Date()));
    const [showNoDate, setShowNoDate] = useState(false);

    const allCards = useMemo(() => flattenCards(board), [board]);

    // Split into dated cards (have a due date and/or start date) and undated.
    const { datedCards, undatedCards } = useMemo(() => {
        const dated = [];
        const undated = [];
        for (const card of allCards) {
            if (card.archived) continue;
            const due = toDay(card.dueDate?.date);
            const start = toDay(card.dueDate?.startDate);
            if (due || start) {
                const end = due || start;
                const begin = start || due;
                dated.push({ card, begin: begin <= end ? begin : end, end: begin <= end ? end : begin });
            } else {
                undated.push(card);
            }
        }
        return { datedCards: dated, undatedCards: undated };
    }, [allCards]);

    // The 6-week grid for the current month.
    const gridDays = useMemo(() => {
        const gridStart = startOfWeek(startOfMonth(cursor));
        const gridEnd = endOfWeek(endOfMonth(cursor));
        return eachDayOfInterval({ start: gridStart, end: gridEnd });
    }, [cursor]);

    // Break the flat day list into week rows.
    const weeks = useMemo(() => {
        const rows = [];
        for (let i = 0; i < gridDays.length; i += 7) rows.push(gridDays.slice(i, i + 7));
        return rows;
    }, [gridDays]);

    const openCard = useCallback((card) => {
        if (card?.permalink) navigate(relative(card.permalink));
    }, [navigate]);

    // All dated cards touching a specific day (for the "+N more" popover).
    const cardsOnDay = useCallback((day) => {
        const d = toDay(day);
        if (!d) return [];
        return datedCards
            .filter(({ begin, end }) => d >= begin && d <= end)
            .map(({ card }) => card);
    }, [datedCards]);

    // Open the shared popover with this day's cards anchored to the "+more" pill.
    const openDayPopover = useCallback((e, day) => {
        handlePopoverClick(e, 'calendarDay', {
            day: day.toISOString(),
            cards: cardsOnDay(day),
            onOpenCard: openCard,
            dotColor: cardColor,
        });
    }, [handlePopoverClick, cardsOnDay, openCard]);

    // --- Reschedule (drag a card onto a day) -----------------------------
    // Optimistically shift the card's dates, then persist via the dedicated
    // /duedate endpoint (the generic card-update endpoint ignores dueDate).
    const rescheduleCard = useCallback(async (cardId, targetDay) => {
        const target = toDay(targetDay);
        if (!target) return;

        // Locate the card + compute the new dueDate (preserving span duration).
        let prevSnapshot = null;
        let hadDueDate = false;
        let newDueDate = null;

        setBoard((prev) => {
            if (!prev?.actionLists) return prev;
            const lists = prev.actionLists.map((list) => {
                const cards = (list.cards || []).map((c) => {
                    if (c._id !== cardId && c.shortId !== cardId) return c;
                    prevSnapshot = c.dueDate ? { ...c.dueDate } : null;
                    hadDueDate = !!c.dueDate?.date;
                    const oldEnd = toDay(c.dueDate?.date) || toDay(c.dueDate?.startDate);
                    const oldStart = toDay(c.dueDate?.startDate);
                    const next = { ...(c.dueDate || {}) };
                    if (oldStart && oldEnd && !isSameDay(oldStart, oldEnd)) {
                        // Span: shift both ends by the same delta, preserve duration.
                        const span = differenceInCalendarDays(oldEnd, oldStart);
                        next.startDate = target;
                        next.date = addDays(target, span);
                    } else {
                        // Single day (or only a start date): set the due date.
                        next.date = target;
                        if (next.startDate) next.startDate = target;
                    }
                    newDueDate = next;
                    return { ...c, dueDate: next };
                });
                return { ...list, cards };
            });
            return { ...prev, actionLists: lists };
        });

        if (!newDueDate) return;

        try {
            // PUT updates an existing due date; POST adds one (No-date tray cards).
            const res = await fetch(`${config.API_URI}/v1/cards/${cardId}/duedate`, {
                method: hadDueDate ? 'PUT' : 'POST',
                headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    date: newDueDate.date,
                    startDate: newDueDate.startDate || undefined,
                    dueTime: newDueDate.dueTime || undefined,
                    reminder: newDueDate.reminder || undefined,
                    status: 'In-Progress',
                }),
            });
            if (!res.ok) throw new Error('reschedule failed');
            const updatedCard = await res.json();
            // Reconcile with the server's dueDate so reload matches.
            setBoard((prev) => {
                if (!prev?.actionLists) return prev;
                const lists = prev.actionLists.map((list) => ({
                    ...list,
                    cards: (list.cards || []).map((c) =>
                        (c._id === cardId || c.shortId === cardId)
                            ? { ...c, dueDate: updatedCard.dueDate || newDueDate }
                            : c
                    ),
                }));
                return { ...prev, actionLists: lists };
            });
        } catch (err) {
            // Revert optimistic change.
            setBoard((prev) => {
                if (!prev?.actionLists) return prev;
                const lists = prev.actionLists.map((list) => ({
                    ...list,
                    cards: (list.cards || []).map((c) =>
                        (c._id === cardId || c.shortId === cardId) ? { ...c, dueDate: prevSnapshot } : c
                    ),
                }));
                return { ...prev, actionLists: lists };
            });
            dispatch(showSnackBar({ message: 'Could not reschedule the card', type: 'error' }));
        }
    }, [dispatch, setBoard]);

    // --- Quick-create a card on a given day --------------------------------
    // New cards go into the board's first list with their due date set to the
    // clicked day. Optimistic insert, then reconcile with the server card.
    const firstList = board?.actionLists?.[0] || null;

    const createCardOnDay = useCallback(async (day, title) => {
        const trimmed = (title || '').trim();
        if (!trimmed || !firstList?._id) return;
        const due = toDay(day);

        const optimisticId = `temp-${trimmed}-${due.getTime()}`;
        const optimisticCard = {
            _id: optimisticId,
            shortId: optimisticId,
            title: trimmed,
            dueDate: { date: due },
            labels: [],
            users: [],
            actionList: { _id: firstList._id },
        };

        setBoard((prev) => {
            if (!prev?.actionLists) return prev;
            return {
                ...prev,
                actionLists: prev.actionLists.map((l) =>
                    l._id === firstList._id ? { ...l, cards: [...(l.cards || []), optimisticCard] } : l
                ),
            };
        });

        try {
            const res = await fetch(`${config.API_URI}/v1/cards`, {
                method: 'POST',
                headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    actionListId: firstList._id,
                    title: trimmed,
                    users: [],
                    dueDate: { date: due },
                }),
            });
            if (!res.ok) throw new Error('create failed');
            const { card: newCard } = await res.json();

            setBoard((prev) => {
                if (!prev?.actionLists) return prev;
                return {
                    ...prev,
                    actionLists: prev.actionLists.map((l) => {
                        if (l._id !== firstList._id) return l;
                        const cards = (l.cards || []).map((c) =>
                            c._id === optimisticId
                                ? { ...newCard, actionList: { _id: l._id } }
                                : c
                        );
                        return { ...l, cards };
                    }),
                };
            });
        } catch (err) {
            setBoard((prev) => {
                if (!prev?.actionLists) return prev;
                return {
                    ...prev,
                    actionLists: prev.actionLists.map((l) => ({
                        ...l,
                        cards: (l.cards || []).filter((c) => c._id !== optimisticId),
                    })),
                };
            });
            dispatch(showSnackBar({ message: 'Could not create the card', type: 'error' }));
        }
    }, [firstList, setBoard, dispatch]);

    const canCreate = !isReadOnly && !!firstList;

    const hasUndated = undatedCards.length > 0;
    const sidebarOpen = hasUndated && showNoDate;

    return (
        <div
            className={`board-calendar${sidebarOpen ? ' has-sidebar' : ''}`}
            style={{ height: `calc(100vh - ${navbarHeight + 49}px)` }}
        >
            <div className="board-calendar__main">
                <div className="board-calendar__header">
                    <div className="board-calendar__nav">
                        <button
                            type="button"
                            className="board-calendar__nav-btn"
                            onClick={() => setCursor((c) => subMonths(c, 1))}
                            aria-label="Previous month"
                        >
                            <KeyboardArrowDown size={18} className="rotate-right" />
                        </button>
                        <h3 className="board-calendar__title">{format(cursor, 'MMMM yyyy')}</h3>
                        <button
                            type="button"
                            className="board-calendar__nav-btn"
                            onClick={() => setCursor((c) => addMonths(c, 1))}
                            aria-label="Next month"
                        >
                            <KeyboardArrowDown size={18} className="rotate-left" />
                        </button>
                    </div>
                    <div className="board-calendar__header-actions">
                        <button
                            type="button"
                            className="board-calendar__today-btn"
                            onClick={() => setCursor(startOfMonth(new Date()))}
                        >
                            Today
                        </button>
                        {hasUndated && (
                            <button
                                type="button"
                                className={`board-calendar__unscheduled-btn${sidebarOpen ? ' active' : ''}`}
                                onClick={() => setShowNoDate((s) => !s)}
                            >
                                Unscheduled
                                <span className="board-calendar__unscheduled-count">{undatedCards.length}</span>
                            </button>
                        )}
                    </div>
                </div>

                <div className="board-calendar__weekdays">
                    {WEEKDAYS.map((d) => (
                        <div key={d} className="board-calendar__weekday">{d}</div>
                    ))}
                </div>

                <div className="board-calendar__grid">
                    {weeks.map((week, wi) => (
                        <CalendarWeekRow
                            key={wi}
                            week={week}
                            cursor={cursor}
                            datedCards={datedCards}
                            isReadOnly={isReadOnly}
                            canCreate={canCreate}
                            onOpenCard={openCard}
                            onReschedule={rescheduleCard}
                            onCreate={createCardOnDay}
                            onShowDay={openDayPopover}
                        />
                    ))}
                </div>
            </div>

            {sidebarOpen && (
                <aside className="board-calendar__sidebar">
                    <div className="board-calendar__sidebar-head">
                        <span className="board-calendar__sidebar-title">
                            Unscheduled <span className="board-calendar__sidebar-count">{undatedCards.length}</span>
                        </span>
                        <button
                            type="button"
                            className="board-calendar__sidebar-close"
                            onClick={() => setShowNoDate(false)}
                            aria-label="Close"
                        >
                            <Close size={16} />
                        </button>
                    </div>
                    {!isReadOnly && (
                        <p className="board-calendar__sidebar-hint">Drag a card onto a day to schedule it.</p>
                    )}
                    <div className="board-calendar__sidebar-list">
                        {undatedCards.map((card) => (
                            <DraggableCardChip
                                key={card._id || card.shortId}
                                card={card}
                                isReadOnly={isReadOnly}
                                onOpen={onOpenCardSafe(openCard, card)}
                            />
                        ))}
                    </div>
                </aside>
            )}
        </div>
    );
};

// Small helper so the chip's onOpen is a stable-ish callback per card.
const onOpenCardSafe = (openCard, card) => () => openCard(card);

// --- A single week row: renders day cells + the spanning bars for that week ---
const CalendarWeekRow = ({ week, cursor, datedCards, isReadOnly, canCreate, onOpenCard, onReschedule, onCreate, onShowDay }) => {
    const weekStart = week[0];
    const weekEnd = week[6];

    // Items intersecting this week, clipped to the week's [start,end], each
    // assigned a stable lane (grid row) so bars don't overlap.
    const { laidOut, perDayCount } = useMemo(() => {
        const items = [];
        for (const { card, begin, end } of datedCards) {
            if (end < weekStart || begin > weekEnd) continue;
            const clipStart = begin < weekStart ? weekStart : begin;
            const clipEnd = end > weekEnd ? weekEnd : end;
            const startCol = differenceInCalendarDays(clipStart, weekStart); // 0..6
            const endCol = differenceInCalendarDays(clipEnd, weekStart);     // 0..6
            items.push({
                card,
                startCol,
                span: endCol - startCol + 1,
                endCol,
                isSingle: isSameDay(begin, end),
                clippedLeft: begin < weekStart,
                clippedRight: end > weekEnd,
            });
        }
        items.sort((a, b) => a.startCol - b.startCol || b.span - a.span);

        // Lane assignment: first free lane whose columns are unoccupied.
        const lanes = []; // lanes[lane] = Set of occupied cols
        items.forEach((it) => {
            let lane = 0;
            for (;;) {
                if (!lanes[lane]) lanes[lane] = new Set();
                let free = true;
                for (let c = it.startCol; c <= it.endCol; c += 1) {
                    if (lanes[lane].has(c)) { free = false; break; }
                }
                if (free) {
                    for (let c = it.startCol; c <= it.endCol; c += 1) lanes[lane].add(c);
                    it.lane = lane;
                    break;
                }
                lane += 1;
            }
        });

        // How many items occupy each column (day), for per-day "+N more".
        const perDay = new Array(7).fill(0);
        items.forEach((it) => {
            for (let c = it.startCol; c <= it.endCol; c += 1) perDay[c] += 1;
        });

        return { laidOut: items, perDayCount: perDay };
    }, [datedCards, weekStart, weekEnd]);

    // Only show bars whose lane fits; a day with hidden items gets "+N more".
    const visibleItems = laidOut.filter((it) => it.lane < MAX_ROWS_PER_DAY);

    return (
        <div className="board-calendar__week">
            <div className="board-calendar__day-cells">
                {week.map((day, col) => {
                    const hiddenOnDay = Math.max(0, perDayCount[col] - MAX_ROWS_PER_DAY);
                    return (
                        <CalendarDayCell
                            key={day.toISOString()}
                            day={day}
                            inMonth={isSameMonth(day, cursor)}
                            isReadOnly={isReadOnly}
                            canCreate={canCreate}
                            moreCount={hiddenOnDay}
                            onReschedule={onReschedule}
                            onCreate={onCreate}
                            onShowDay={onShowDay}
                        />
                    );
                })}
            </div>

            <div className="board-calendar__bars">
                {visibleItems.map((item, idx) => (
                    <DraggableSpanBar
                        key={(item.card._id || item.card.shortId) + ':' + idx}
                        item={item}
                        isReadOnly={isReadOnly}
                        onOpen={() => onOpenCard(item.card)}
                    />
                ))}
            </div>
        </div>
    );
};

// A day cell: a drop target for reschedule + quick-add a card on this day.
const CalendarDayCell = ({ day, inMonth, isReadOnly, canCreate, moreCount = 0, onReschedule, onCreate, onShowDay }) => {
    const ref = useRef(null);
    const inputRef = useRef(null);
    const [isOver, setIsOver] = useState(false);
    const [adding, setAdding] = useState(false);
    const [title, setTitle] = useState('');

    useEffect(() => {
        const el = ref.current;
        if (!el || isReadOnly) return;
        return dropTargetForElements({
            element: el,
            getData: () => ({ day: day.toISOString() }),
            onDragEnter: () => setIsOver(true),
            onDragLeave: () => setIsOver(false),
            onDrop: ({ source }) => {
                setIsOver(false);
                const cardId = source?.data?.cardId;
                if (cardId) onReschedule(cardId, day);
            },
        });
    }, [day, isReadOnly, onReschedule]);

    useEffect(() => {
        if (adding) setTimeout(() => inputRef.current?.focus(), 0);
    }, [adding]);

    const submit = () => {
        const t = title.trim();
        if (t) onCreate(day, t);
        setTitle('');
        setAdding(false);
    };

    return (
        <div
            ref={ref}
            className={[
                'board-calendar__day',
                inMonth ? '' : 'is-muted',
                isToday(day) ? 'is-today' : '',
                isOver ? 'is-drop-over' : '',
            ].filter(Boolean).join(' ')}
        >
            <div className="board-calendar__day-head">
                <span className="board-calendar__day-num">{format(day, 'd')}</span>
                {canCreate && !adding && (
                    <button
                        type="button"
                        className="board-calendar__day-add"
                        onClick={() => setAdding(true)}
                        aria-label="Add a card on this day"
                        title="Add a card"
                    >
                        <Add size={18} />
                    </button>
                )}
            </div>

            {adding && (
                <input
                    ref={inputRef}
                    className="board-calendar__day-input"
                    value={title}
                    placeholder="Card title…"
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={submit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') submit();
                        if (e.key === 'Escape') { setTitle(''); setAdding(false); }
                    }}
                />
            )}

            {moreCount > 0 && (
                <button
                    type="button"
                    id={`cal-more-${day.toISOString()}`}
                    className="board-calendar__day-more"
                    data-popover-trigger
                    onClick={(e) => onShowDay(e, day)}
                >
                    +{moreCount} more
                </button>
            )}
        </div>
    );
};

// A spanning bar (multi-day) or single-day chip, draggable to reschedule.
const DraggableSpanBar = ({ item, isReadOnly, onOpen }) => {
    const ref = useRef(null);
    const [dragging, setDragging] = useState(false);
    const { card, startCol, span, lane, isSingle, clippedLeft, clippedRight } = item;

    useEffect(() => {
        const el = ref.current;
        if (!el || isReadOnly) return;
        return combine(
            draggable({
                element: el,
                getInitialData: () => ({ cardId: card.shortId || card._id }),
                onDragStart: () => setDragging(true),
                onDrop: () => setDragging(false),
            })
        );
    }, [card._id, card.shortId, isReadOnly]);

    const style = {
        gridColumn: `${startCol + 1} / span ${span}`,
        gridRow: lane + 1, // stable lane so spanning bars never overlap
        '--cal-bar-color': cardColor(card),
    };

    return (
        <div
            ref={ref}
            className={[
                'board-calendar__bar',
                isSingle ? 'is-chip' : 'is-span',
                clippedLeft ? 'clip-left' : '',
                clippedRight ? 'clip-right' : '',
                dragging ? 'is-dragging' : '',
                isReadOnly ? '' : 'is-draggable',
            ].filter(Boolean).join(' ')}
            style={style}
            onClick={onOpen}
            title={card.title}
        >
            <span className="board-calendar__bar-title">{card.title}</span>
        </div>
    );
};

// A simple draggable chip (used in the No-date tray).
const DraggableCardChip = ({ card, isReadOnly, onOpen }) => {
    const ref = useRef(null);
    const [dragging, setDragging] = useState(false);

    useEffect(() => {
        const el = ref.current;
        if (!el || isReadOnly) return;
        return draggable({
            element: el,
            getInitialData: () => ({ cardId: card.shortId || card._id }),
            onDragStart: () => setDragging(true),
            onDrop: () => setDragging(false),
        });
    }, [card._id, card.shortId, isReadOnly]);

    return (
        <div
            ref={ref}
            className={`board-calendar__nodate-chip${dragging ? ' is-dragging' : ''}${isReadOnly ? '' : ' is-draggable'}`}
            style={{ '--cal-bar-color': cardColor(card) }}
            onClick={onOpen}
            title={card.title}
        >
            <span className="board-calendar__bar-title">{card.title}</span>
        </div>
    );
};


export default BoardCalendarView;
